import {
	CardShell,
	ListSkeleton,
	SectionCard,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@atlas/ui-components'
import { Badge, Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import type { WorkflowDTO } from '@/domain/workflow-admin.types'
import {
	useActivateWorkflow,
	useCreateWorkflow,
	useDeactivateWorkflow,
	useDeleteWorkflow,
	useSetDefaultWorkflow,
	useWorkflows,
} from '@/hooks/useWorkflowAdmin'
import { TextField } from '@/pages/Classification/FormField'
import { EmptyState, ErrorBanner, ErrorState } from './states'

/**
 * Workflow administration home: every workflow for the tenant, its lifecycle
 * state, and which one is the tenant default. Lifecycle is changed through the
 * dedicated operations only — the metadata form never carries `isActive` or
 * `isDefault`, because the backend rejects them there.
 */

function CreateWorkflowForm({ onClose }: { onClose: () => void }) {
	const create = useCreateWorkflow()
	const [form, setForm] = useState({ code: '', name: '' })
	const canSubmit = form.code.trim().length > 0 && form.name.trim().length > 0 && !create.isPending
	return (
		<SectionCard
			title="New workflow"
			description="Created inactive and non-default. Activate it once its statuses are configured."
		>
			<div className="flex flex-wrap items-end gap-3">
				<TextField
					label="Code"
					value={form.code}
					onChange={(code) => setForm({ ...form, code })}
					placeholder="fast_track"
				/>
				<TextField
					label="Name"
					value={form.name}
					onChange={(name) => setForm({ ...form, name })}
					placeholder="Fast Track"
				/>
				<Button
					isDisabled={!canSubmit}
					onPress={() => create.mutate({ code: form.code.trim(), name: form.name.trim() }, { onSuccess: onClose })}
				>
					{create.isPending ? 'Creating…' : 'Create'}
				</Button>
				<Button variant="outline" onPress={onClose}>
					Cancel
				</Button>
				{create.isError && <ErrorBanner error={create.error} title="Could not create workflow" />}
			</div>
		</SectionCard>
	)
}

interface RowActions {
	onSetDefault: (id: string) => void
	onActivate: (id: string) => void
	onDeactivate: (id: string) => void
	onDelete: (id: string) => void
	pendingId: string | null
}

function WorkflowRow({ workflow, actions }: { workflow: WorkflowDTO; actions: RowActions }) {
	const pending = actions.pendingId === workflow.workflowId
	const deleted = workflow.deletedAt !== null
	return (
		<TableRow data-testid={`workflow-row-${workflow.code}`}>
			<TableCell className="font-medium">{workflow.name}</TableCell>
			<TableCell className="text-ds-text-muted">{workflow.code}</TableCell>
			<TableCell>
				{workflow.isDefault && (
					<Badge size="sm" data-testid={`default-badge-${workflow.code}`}>
						default
					</Badge>
				)}
			</TableCell>
			<TableCell>
				{deleted ? (
					<Badge variant="muted" size="sm">
						deleted
					</Badge>
				) : workflow.isActive ? (
					<Badge variant="secondary" size="sm">
						active
					</Badge>
				) : (
					<Badge variant="muted" size="sm">
						inactive
					</Badge>
				)}
			</TableCell>
			<TableCell className="flex flex-wrap gap-2">
				<Link
					className="text-sm text-ds-primary hover:underline self-center"
					to={`/admin/workflows/${workflow.workflowId}`}
				>
					configure →
				</Link>
				{!deleted && !workflow.isDefault && workflow.isActive && (
					<Button
						variant="outline"
						isDisabled={pending}
						onPress={() => actions.onSetDefault(workflow.workflowId)}
						aria-label={`Make ${workflow.name} the default`}
					>
						{pending ? 'Working…' : 'Make default'}
					</Button>
				)}
				{!deleted &&
					(workflow.isActive ? (
						<Button
							variant="outline"
							isDisabled={pending}
							onPress={() => actions.onDeactivate(workflow.workflowId)}
							aria-label={`Deactivate ${workflow.name}`}
						>
							Deactivate
						</Button>
					) : (
						<Button
							variant="outline"
							isDisabled={pending}
							onPress={() => actions.onActivate(workflow.workflowId)}
							aria-label={`Activate ${workflow.name}`}
						>
							Activate
						</Button>
					))}
				{!deleted && (
					<Button
						variant="outline"
						isDisabled={pending}
						onPress={() => actions.onDelete(workflow.workflowId)}
						aria-label={`Delete ${workflow.name}`}
					>
						Delete
					</Button>
				)}
			</TableCell>
		</TableRow>
	)
}

function WorkflowTable({ workflows, actions }: { workflows: WorkflowDTO[]; actions: RowActions }) {
	return (
		<Table>
			<TableHeader>
				<TableRow>
					<TableHead>Name</TableHead>
					<TableHead>Code</TableHead>
					<TableHead>Default</TableHead>
					<TableHead>State</TableHead>
					<TableHead>Actions</TableHead>
				</TableRow>
			</TableHeader>
			<TableBody>
				{workflows.map((workflow) => (
					<WorkflowRow key={workflow.workflowId} workflow={workflow} actions={actions} />
				))}
			</TableBody>
		</Table>
	)
}

/**
 * The list body: exactly one of the loading, error, empty and populated states.
 * A first-run tenant and a view that simply filtered everything out get
 * different wording, because they need different next actions.
 */
function WorkflowListBody({
	query,
	actions,
	includeDeleted,
}: {
	query: ReturnType<typeof useWorkflows>
	actions: RowActions
	includeDeleted: boolean
}) {
	if (query.isLoading) return <ListSkeleton label="Loading workflows" />
	if (query.isError) {
		return <ErrorState error={query.error} onRetry={() => void query.refetch()} title="Could not load workflows" />
	}
	const workflows = query.data ?? []
	if (workflows.length > 0) return <WorkflowTable workflows={workflows} actions={actions} />
	return (
		<EmptyState
			variant={includeDeleted ? 'filtered' : 'first-run'}
			title={includeDeleted ? 'No workflows match' : 'No workflows configured yet'}
			description={
				includeDeleted
					? 'Nothing matched the current view.'
					: 'Create a workflow, add its statuses, then activate it and make it the tenant default.'
			}
		/>
	)
}

export function WorkflowListPage() {
	const [includeDeleted, setIncludeDeleted] = useState(false)
	const [creating, setCreating] = useState(false)
	const query = useWorkflows(includeDeleted)
	const setDefault = useSetDefaultWorkflow()
	const activate = useActivateWorkflow()
	const deactivate = useDeactivateWorkflow()
	const remove = useDeleteWorkflow()

	const mutations = [setDefault, activate, deactivate, remove]
	const failed = mutations.find((mutation) => mutation.isError)
	const pendingId =
		(setDefault.isPending && setDefault.variables) ||
		(activate.isPending && activate.variables?.id) ||
		(deactivate.isPending && deactivate.variables?.id) ||
		(remove.isPending && remove.variables?.id) ||
		null

	// The backend requires the tenant to always have an active default, so the
	// first workflow activated for a tenant has to claim that role; later ones
	// must leave the existing default alone.
	const hasActiveDefault = (query.data ?? []).some((item) => item.isActive && item.isDefault)

	const actions: RowActions = {
		// Nothing is applied optimistically — a refused change leaves the list, and
		// so the displayed default, exactly as the server last reported it.
		onSetDefault: (id) => setDefault.mutate(id),
		onActivate: (id) => activate.mutate({ id, body: { setAsDefault: !hasActiveDefault } }),
		onDeactivate: (id) => deactivate.mutate({ id, body: {} }),
		onDelete: (id) => remove.mutate({ id, body: {} }),
		pendingId,
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-semibold">Workflows</h1>
					<p className="text-ds-text-muted">Configure the workflows Work Orders resolve into.</p>
				</div>
				<div className="flex items-center gap-3">
					<label className="flex items-center gap-2 text-sm">
						<input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />
						Include deleted
					</label>
					<Link className="text-sm text-ds-primary hover:underline" to="/admin/workflows/resolver-rules">
						Resolver rules
					</Link>
					<Link className="text-sm text-ds-primary hover:underline" to="/admin/workflows/diagnostics">
						Diagnostics
					</Link>
					<Button onPress={() => setCreating((open) => !open)}>{creating ? 'Cancel' : 'New workflow'}</Button>
				</div>
			</div>
			{creating && <CreateWorkflowForm onClose={() => setCreating(false)} />}
			{failed && <ErrorBanner error={failed.error} title="Workflow change was rejected" />}
			<CardShell>
				<WorkflowListBody query={query} actions={actions} includeDeleted={includeDeleted} />
			</CardShell>
		</div>
	)
}
